//
// Copyright 2025 DXOS.org
//

import { useAtomValue } from '@effect/atom-react/Hooks';
import * as Atom from 'effect/reactivity/Atom';
import React, { type PropsWithChildren, Suspense, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { Capabilities } from '../../../common/index.ts';
import { topologicalSort } from '../../../helpers.ts';
import {
  FIRST_INTERACTIVE_EVENT,
  LoadingState,
  type StartupProgress,
  type UseAppOptions,
  useCapabilities,
  useLoading,
} from '../../hooks/index.ts';
import { bootLoader } from './loader.ts';

let relayedGateStatus: string | undefined;

export type AppProps = Pick<UseAppOptions, 'debounce' | 'verboseStatus'> & {
  ready: boolean;
  error: unknown;
  progress?: StartupProgress;
};

export const App = ({ ready, error, debounce, progress, verboseStatus = false }: AppProps) => {
  const reactContexts = useCapabilities(Capabilities.ReactContext);
  const reactRoots = useCapabilities(Capabilities.ReactRoot);
  const sortedContexts = useMemo(() => topologicalSort(reactContexts), [reactContexts]);
  const gates = useCapabilities(Capabilities.BootGate);
  const heldAtom = useMemo(
    () => Atom.make((get) => gates.map((gate) => get(gate.state)).find((state) => state.pending)),
    [gates],
  );
  const held = useAtomValue(heldAtom);
  const stage = useLoading(ready && !held, debounce);
  const placeholderDismissed = stage >= LoadingState.Done;
  // The shell mounts a tick EARLIER than the dismissal, at the same stage that starts the loader's
  // outro: the loader is still on screen (z-index 10) and fading, so the real UI paints beneath it
  // instead of after it — gating both on `Done` leaves a blank frame between the two.
  const shellMounted = stage >= LoadingState.FadeOut;

  // Activation lines are opt-in (`verboseStatus`): they name framework internals, so the default
  // boot log is the host's own phases only. When enabled, one line per plugin (and per framework
  // activation event) rather than per module — a plugin contributes a dozen-odd modules, which
  // scrolled hundreds of lines past the user. The framework leaves both choices to the host.
  const announcedRef = useRef(new Set<string>());

  // Relay the startup lifecycle into the boot loader injected by
  // `@dxos/app-framework/vite-plugin` (a Solid app inlined into `index.html`,
  // the only visible loading UI). Plugin activation fills the `[0.5, 1]` half
  // of the ring; the raw `event` / `module` ids ride along so the loader owns
  // how each transition is rendered and traced. No-op once the outro has
  // started (`FadeOut`), so the dismissal runs uncontended.
  useEffect(() => {
    if (stage >= LoadingState.FadeOut) {
      return;
    }

    const fraction = progress?.progress ?? 0;
    if (!held) {
      bootLoader?.progress(0.5 + fraction * 0.5);
    }
    if (progress?.pluginSlug) {
      // Optional call — see the note on `plugins?.(...)` in `useApp`.
      bootLoader?.activated?.(progress.pluginSlug);
    }

    // Plugin-level transitions collapse to the plugin's own name; event-level ones (no
    // `pluginName`) are the framework's own phases and are already few.
    const humanized = progress?.pluginName ?? progress?.humanizedName;
    if (!verboseStatus || !humanized) {
      return;
    }

    const key = progress?.pluginName ?? `event:${progress?.event ?? humanized}`;
    if (announcedRef.current.has(key)) {
      return;
    }
    announcedRef.current.add(key);
    bootLoader?.status({
      event: progress?.event,
      module: progress?.module,
      humanized: `Activating ${humanized}`,
    });
  }, [
    stage,
    held,
    progress?.progress,
    progress?.event,
    progress?.module,
    progress?.humanizedName,
    progress?.pluginName,
    progress?.pluginSlug,
    verboseStatus,
  ]);

  useEffect(() => {
    if (!held || stage >= LoadingState.FadeOut) {
      return;
    }
    if (held.status && held.status !== relayedGateStatus) {
      relayedGateStatus = held.status;
      bootLoader?.status({ humanized: held.status });
    }
    if (held.status && held.count) {
      bootLoader?.status({ humanized: held.status, range: { index: held.count.done, total: held.count.total } });
    }
    if (held.progress !== undefined) {
      bootLoader?.progress(held.progress);
    }
  }, [held, stage]);

  // Hand off at fade-out: play the loader's graceful shrink-and-fade outro.
  // `useLayoutEffect` runs before the next paint so the outro begins in the
  // same frame the real shell starts rendering beneath it.
  useLayoutEffect(() => {
    if (stage >= LoadingState.FadeOut) {
      bootLoader?.ready();
    }
  }, [stage]);

  // Emit a once-per-app `app-framework:first-interactive` mark the first time
  // the loader is dismissed and the real app shell renders. Closes the gap
  // between `Startup` activated and the first interactive paint.
  //
  // Also the framework-owned handoff-complete signal: `dismiss()` removes the
  // loader immediately on the fast-load path (where `useLoading` skips
  // `FadeOut`), and is a no-op once an outro is already in flight, so it never
  // cuts the animation short.
  useEffect(() => {
    if (!placeholderDismissed) {
      return;
    }
    if (performance.getEntriesByName(FIRST_INTERACTIVE_EVENT).length === 0) {
      performance.mark(FIRST_INTERACTIVE_EVENT);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(FIRST_INTERACTIVE_EVENT, { detail: Math.round(performance.now()) }));
      }
    }
    bootLoader?.dismiss();
  }, [placeholderDismissed]);

  // Used in tests to exercise the error boundary & reset dialog (see
  // composer-app's `basic.spec.ts`). Thrown into the surrounding `ErrorBoundary`.
  if (location.search === '?throw') {
    throw new Error('Test error');
  }

  if (error) {
    // This triggers the error boundary to provide UI feedback for the startup error.
    throw error;
  }

  // The boot loader owns the screen until its outro starts; render nothing
  // until then (any DOM here would sit invisibly behind the `z-index: 10`
  // loader anyway).
  // TODO(wittjosiah): Consider using Suspense instead.
  if (!shellMounted) {
    return null;
  }

  return (
    // Contexts nest, so one suspending provider necessarily withholds everything below it; the
    // boundary here at least keeps that from unwinding past the app's providers.
    <Suspense fallback={null}>
      <ContextChain contexts={sortedContexts}>
        {reactRoots.map(({ id, root: Component }) => (
          // One boundary per root: roots read capabilities whose providers activate in a later
          // wave, and `useCapability` suspends on that. A shared boundary would let one late root
          // withhold every other root's already-renderable content — the blank shell this fixes.
          <Suspense key={id} fallback={null}>
            <Component />
          </Suspense>
        ))}
      </ContextChain>
    </Suspense>
  );
};

/**
 * Nests each provider around the next at render time so the element type is a constant: composing
 * the nesting into a component makes it a new type per render, remounting the whole app on any
 * capability change. A contributed context appends, so the chain extends inward and the providers
 * above keep their positions; a removed one still shifts everything below it.
 */
const ContextChain = ({ contexts, children }: PropsWithChildren<{ contexts: Capabilities.ReactContext[] }>) => {
  if (contexts.length === 0) {
    return <>{children}</>;
  }

  const [{ context: Context }, ...rest] = contexts;
  return (
    <Context>
      <ContextChain contexts={rest}>{children}</ContextChain>
    </Context>
  );
};
